import { MapEvent, MapMouseEvent } from 'mapbox-gl';
import { FC, useCallback, useEffect, useRef } from 'react';
import { MapRef } from 'react-map-gl';

import type { MergedDepartmentsGeoJson } from '../../../lib/utils/gp-surfaces-geojson';

import {
  DepartmentFeatureProperties,
  FRANCE_DEPT_FILL_LAYER_ID,
  FranceDepartmentsMapboxLayer,
  GeocoderControl,
  PfMap,
  StyleControl,
  zoomOnFeature,
} from '@map';

export type GpSurfacesMapboxViewProps = {
  data: MergedDepartmentsGeoJson;
  hoveredCode: string | null;
  selectedCode: string | null;
  onHover: (code: string | null) => void;
  onSelect: (properties: DepartmentFeatureProperties | null) => void;
};

export const GpSurfacesMapboxView: FC<GpSurfacesMapboxViewProps> = ({
  data,
  hoveredCode,
  selectedCode,
  onHover,
  onSelect,
}) => {
  const mapRef = useRef<MapRef>(null);

  const fitFranceBounds = useCallback(
    (animate = true) => {
      const map = mapRef.current?.getMap();
      if (!map || data.features.length === 0) {
        return;
      }
      zoomOnFeature({
        animate,
        geojson: data,
        map,
        padding: 40,
      });
    },
    [data],
  );

  const handleLoad = useCallback(
    (event: MapEvent) => {
      fitFranceBounds(false);
      event.target.getCanvas().style.cursor = '';
    },
    [fitFranceBounds],
  );

  useEffect(() => {
    fitFranceBounds();
  }, [fitFranceBounds]);

  const handleMouseMove = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      const code = feature?.properties?.code;
      onHover(typeof code === 'string' ? code : null);
      event.target.getCanvas().style.cursor = feature ? 'pointer' : '';
    },
    [onHover],
  );

  const handleMouseLeave = useCallback(() => {
    onHover(null);
    const map = mapRef.current?.getMap();
    if (map) {
      map.getCanvas().style.cursor = '';
    }
  }, [onHover]);

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature?.properties) {
        onSelect(null);
        return;
      }
      onSelect(feature.properties as DepartmentFeatureProperties);
    },
    [onSelect],
  );

  return (
    <PfMap
      interactiveLayerIds={[FRANCE_DEPT_FILL_LAYER_ID]}
      onClick={handleClick}
      onLoad={handleLoad}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      ref={mapRef}
    >
      <FranceDepartmentsMapboxLayer
        data={data}
        hoveredCode={hoveredCode}
        selectedCode={selectedCode}
      />
      <GeocoderControl />
      <StyleControl />
    </PfMap>
  );
};
